import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalTitle, ModalFoot, Pip } from 'components/Modal';

/**
 * Generic info / notice dialog. One component covers booking success,
 * event context, and destructive confirms by switching `variant`.
 *
 * Existing props (unchanged): isOpen, closeModal, title, content,
 *   hasButton, panelClassName
 * New optional props (backward-compatible):
 *   variant          'success' | 'info' | 'danger'   default 'info'
 *   eyebrow          string                           (uppercase meta)
 *   primaryAction    { label, onClick, tone? }        footer primary
 *   secondaryAction  { label, onClick }               footer ghost
 *
 * When `primaryAction`/`secondaryAction` are omitted the modal falls
 * back to the legacy single-button form (driven by `hasButton`).
 */
const ModalDialog = ({
  isOpen,
  closeModal,
  title,
  content,
  hasButton,
  panelClassName = '',
  variant = 'info',
  eyebrow,
  primaryAction,
  secondaryAction,
  hideIcon = false,
  size = 'sm',
}) => {
  const { t } = useTranslation();

  const hasFooterActions = !!(primaryAction || secondaryAction || hasButton);
  const isStringContent = typeof content === 'string';

  return (
    <Modal
      isOpen={isOpen}
      onClose={closeModal}
      size={size}
      className={panelClassName}
    >
      <div className="flex items-start gap-4 px-6 sm:px-9 pt-8 pb-4">
        {!hideIcon && <Pip variant={variant} />}
        <div className="min-w-0 flex-1 pr-10">
          {eyebrow && (
            <div className="text-[12.5px] font-display font-semibold uppercase tracking-[0.1em] text-muted mb-1.5">
              {eyebrow}
            </div>
          )}
          <ModalTitle size="md">{title}</ModalTitle>
        </div>
      </div>

      <div
        className={`px-6 sm:px-9 pb-6 max-h-[50vh] overflow-y-auto text-[15px] leading-[1.6] text-ink-2 ${
          isStringContent ? 'whitespace-pre-wrap [overflow-wrap:anywhere]' : ''
        }`}
      >
        {content}
      </div>

      {hasFooterActions && (
        <ModalFoot className="justify-end">
          {secondaryAction && (
            <button
              type="button"
              onClick={secondaryAction.onClick}
              className="rounded-full px-5 py-3 text-sm font-semibold text-ink hover:bg-ink/5 transition"
            >
              {secondaryAction.label}
            </button>
          )}
          {primaryAction ? (
            <button
              type="button"
              onClick={primaryAction.onClick}
              className={`rounded-full px-5 py-3 text-sm font-semibold text-white transition ${
                primaryAction.tone === 'danger'
                  ? 'bg-bad hover:bg-bad/90'
                  : 'bg-ink hover:bg-black'
              }`}
            >
              {primaryAction.label}
            </button>
          ) : hasButton ? (
            <button
              type="button"
              onClick={closeModal}
              className="rounded-full bg-ink text-white text-sm font-semibold px-5 py-3 hover:bg-black transition"
            >
              {t('event_detail.back_dashboard')}
            </button>
          ) : null}
        </ModalFoot>
      )}
    </Modal>
  );
};

export default ModalDialog;
